import {
  Accordion,
  AccordionItem,
  AccordionButton,
  Box,
  Flex,
  Icon,
  IconButton,
  Image,
  Stack,
  Text,
  Tooltip,
  theme,
  useColorMode,
  useColorModeValue,
} from "@chakra-ui/react";
import React, { useContext, useEffect, useState } from "react";
import { navItems } from "../../utils/Data/data";
import AuthContext from "../Auth/AuthContext";
import CustomAccordion, { hasActiveChild } from "./CustomAccordion";
import { SidebarUserDetails } from "./SideBar";
import SearchInput from "./SearchInput";
import { AgencyContext } from "../Settings/AgencyProvider/AgencyProvider";
import { debounce } from "lodash";
import { getHostDomain } from "../../utils/utils";
import { fetchController } from "../../utils/FetchController/fetchController";
import { Link, useLocation } from "react-router-dom";
import { FiHome } from "react-icons/fi";
import { MdChevronLeft, MdChevronRight } from "react-icons/md";
import TeamSwitcher from "./TeamSwitcher";

const NewSideBar = ({ collapsed = false, onToggleCollapsed }) => {
  const baseUrl = getHostDomain();
  const { colorMode } = useColorMode();

  const { authUser, logout } = useContext(AuthContext);
  const userName =
    authUser?.first_name?.length > 15
      ? `${authUser?.first_name?.slice(0, 10)}...`
      : authUser?.first_name;
  const onClickLogout = () => {
    logout();
  };

  const [options, setOptions] = useState([]);
  const [text, setText] = useState("");
  const [tag, setTag] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const debouncedFetch = debounce(async (query) => {
      if (query.trim().length < 3) {
        setOptions([]);
        return;
      }

      const urlHit = `${baseUrl}/api/v1/link_tag_entity/search/entities?tag_term=${query}&page=1&size=50`;

      try {
        setLoading(true);
        const response = await fetchController(urlHit, "GET");
        setOptions(response.data.items[0]?.entities || []);
        setTag(response.data.items[0]?.tag);
      } catch (error) {
        console.error("Error fetching data:", error);
        setOptions([]);
      } finally {
        setLoading(false);
      }
    }, 500);

    debouncedFetch(text);

    return () => debouncedFetch.cancel();
  }, [text, baseUrl]);
  function onTextChange(text) {
    setText(text);
  }
  function onChange(value) {
    setText(value);
  }

  const agency = useContext(AgencyContext);
  const { myDetails } = agency;
  const agencyLogoUrl = myDetails ? myDetails?.branding?.logo_url : null;
  const logo = !agency.loading ? (
    myDetails?.agency_id ? (
      agencyLogoUrl ? (
        <Image
          width={collapsed ? "32px" : "100px"}
          src={
            agencyLogoUrl.includes(window.origin)
              ? agencyLogoUrl
              : `${window.location.origin}${agencyLogoUrl}`
          }
          alt={`${myDetails?.branding?.agency_name}-logo`}
        />
      ) : null
    ) : (
      <Image
        width={collapsed ? "32px" : "150px"}
        src={collapsed ? "/logo/favicon.ico" : "/logo/indightoai.png"}
        alt="Insighto.ai"
        objectFit="contain"
      />
    )
  ) : null;

  const location = useLocation();

  const isAnyNavItemActive = navItems.some((navItem) =>
    hasActiveChild(navItem, location.pathname)
  );
  const textColor = agency?.textColor;
  const iconColor = useColorModeValue(
    textColor || theme.colors.gray[500],
    "white"
  );
  const isActive = location.pathname.includes(window.location.path);
  const activeTitleColor =
    colorMode === "light"
      ? isActive
        ? textColor || "black"
        : "gray"
      : "white";

  // Top-level navItem icon (collapsed view): pick the icon of the first
  // child that has one, falling back to a neutral icon.
  const firstIconOf = (navItem) => {
    if (navItem.icon) return navItem.icon;
    const stack = [...(navItem.children || [])];
    while (stack.length) {
      const c = stack.shift();
      if (c.icon) return c.icon;
      if (c.children?.length) stack.push(...c.children);
    }
    return FiHome;
  };

  const visibleNavItems = navItems.filter((navItem) => {
    if (navItem.id === "manage_agency") {
      if (myDetails?.agency_id) return false;
      const activeTeamId = localStorage.getItem("activeTeamId");
      if (activeTeamId && activeTeamId !== "personal") return false;
      return true;
    }
    if (navItem.id === "insighto_admin") {
      const userRoles = authUser?.roles || [];
      return userRoles.includes("insighto_admin");
    }
    return true;
  });

  return (
    <Flex
      justifyContent={"center"}
      alignItems={"space-between"}
      flexDir={"column"}
      gap={3}
      h={"100%"}
      w={"100%"}
    >
      <Stack
        p={2}
        justifyContent={"space-between"}
        alignItems="center"
        gap={2}
      >
        <Flex
          align="center"
          justify={collapsed ? "center" : "space-between"}
          w="100%"
          gap={2}
        >
          <Box style={{ color: "#3182ce" }}>{logo}</Box>
          <Tooltip
            label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            placement="right"
          >
            <IconButton
              aria-label="Toggle sidebar"
              size="xs"
              variant="ghost"
              icon={collapsed ? <MdChevronRight /> : <MdChevronLeft />}
              onClick={onToggleCollapsed}
            />
          </Tooltip>
        </Flex>

        {!collapsed && (
          <>
            <SearchInput
              options={options}
              value={text}
              onChange={onChange}
              onTextChange={onTextChange}
              loading={loading}
              tag={tag}
            />
            <TeamSwitcher />
          </>
        )}
      </Stack>
      <Box
        overflowY={"auto"}
        w={"100%"}
        flex={1}
        borderRadius={"md"}
        sx={{
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": {
            display: "none",
          },
        }}
      >
        {/* Overview / dashboard link */}
        <Accordion w={"100%"}>
          <AccordionItem
            bg={isActive ? "gray.200" : undefined}
            borderBottomWidth={"0px !important"}
          >
            <AccordionButton
              px={collapsed ? 2 : 4}
              py={2}
              justifyContent={collapsed ? "center" : "space-between"}
            >
              <Link
                to={"/dashboard"}
                style={{ width: "100%", textAlign: "left" }}
              >
                {collapsed ? (
                  <Tooltip label="Overview" placement="right">
                    <Flex justify="center">
                      <Icon as={FiHome} boxSize={5} color={iconColor} />
                    </Flex>
                  </Tooltip>
                ) : (
                  <Flex alignItems={"center"} gap={3}>
                    <Icon as={FiHome} size="md" color={iconColor} />
                    <Text fontWeight={"bold"} color={activeTitleColor}>
                      Overview
                    </Text>
                  </Flex>
                )}
              </Link>
            </AccordionButton>
          </AccordionItem>
        </Accordion>

        {/* In collapsed mode show a compact icon rail; clicking an
            icon temporarily expands the sidebar so the user can pick
            a child. */}
        {collapsed
          ? visibleNavItems.map((navItem) => {
              const IconCmp = firstIconOf(navItem);
              return (
                <Tooltip
                  key={navItem.id}
                  label={navItem.title}
                  placement="right"
                >
                  <Flex
                    align="center"
                    justify="center"
                    py={3}
                    cursor="pointer"
                    _hover={{ bg: "blackAlpha.50" }}
                    onClick={onToggleCollapsed}
                  >
                    <Icon as={IconCmp} boxSize={5} color={iconColor} />
                  </Flex>
                </Tooltip>
              );
            })
          : visibleNavItems.map((navItem, index) => (
              <CustomAccordion
                key={index}
                navItem={navItem}
                forceOpen={
                  !isAnyNavItemActive && navItem.title === "Build"
                }
              />
            ))}
      </Box>

      <SidebarUserDetails
        navSize={"large"}
        user={authUser}
        onClickLogout={onClickLogout}
        userName={userName}
        collapsed={collapsed}
      />
    </Flex>
  );
};

export default NewSideBar;
